import { useState, useEffect, useCallback } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import toast from "react-hot-toast";
import {
  IPermission,
  ICreatePermission,
  IUpdatePermission,
  IAdminWithPermissions,
  IAdminUser,
  IUsePermissionsReturn,
  IUseAdminPermissionsReturn,
  IPermissionApiResponse,
  IAdminPermissionApiResponse
} from "./permission.types";

// Hook for managing permissions
export const usePermissions = (): IUsePermissionsReturn => {
  const [permissions, setPermissions] = useState<IPermission[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: IPermissionApiResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.permissions,
      });

      if (response.success && response.data?.permissions) {
        setPermissions(response.data.permissions);
      } else {
        throw new Error(response.message || "Failed to fetch permissions");
      }
    } catch (error: any) {
      console.error("Error fetching permissions:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch permissions";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);


  const getPermissionById = useCallback((id: string): IPermission | undefined => {
    return permissions.find(perm => perm._id === id);
  }, [permissions]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    fetchPermissions,
    getPermissionById
  };
};

// Hook for managing admin permissions
export const useAdminPermissions = (): IUseAdminPermissionsReturn => {
  const [adminsWithPermissions, setAdminsWithPermissions] = useState<IAdminWithPermissions[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAdminsWithPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: IAdminPermissionApiResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.adminPermissions,
      });

      if (response.success && Array.isArray(response.data)) {
        setAdminsWithPermissions(response.data);
      } else {
        throw new Error("Failed to fetch admin permissions");
      }
    } catch (error: any) {
      console.error("Error fetching admin permissions:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch admin permissions";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const getAdminPermissions = useCallback(async (adminId: string): Promise<{ admin: IAdminUser; permissions: IPermission[] }> => {
    try {
      const response: IAdminPermissionApiResponse = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.adminPermissions}/${adminId}`,
      });

      if (response.success && response.data && !Array.isArray(response.data)) {
        // If response.data.admin is a string, you need to fetch the full admin user or map it accordingly.
        if (typeof response.data.admin === "string") {
          // You may need to fetch the admin user by ID here.
          // For now, return a placeholder or throw an error.
          throw new Error("Admin user details are not populated in the response.");
        }
        return response.data as { admin: IAdminUser; permissions: IPermission[] };
      } else {
        throw new Error(response.message || "Failed to fetch admin permissions");
      }
    } catch (error: any) {
      console.error("Error fetching admin permissions:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch admin permissions";
      setError(errorMessage);
      // Don't show toast here, let the component handle it
      throw error;
    }
  }, []);

  const assignPermissionsToAdmin = useCallback(async (adminId: string, permissionIds: string[]) => {
    setLoading(true);
    setError(null);
    
    try {
      const response: IAdminPermissionApiResponse = await ApiService({
        method: "POST",
        endpoint: `${apiPaths.adminPermissions}/${adminId}`,
        data: { permissionIds }
      });

      if (response.success) {
        // Refresh the admin permissions list
        await fetchAdminsWithPermissions();
        // toast.success(response.message || "Permissions assigned successfully");
      } else {
        throw new Error(response.message || "Failed to assign permissions");
      }
    } catch (error: any) {
      console.error("Error assigning permissions:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to assign permissions";
      setError(errorMessage);
      // Don't show toast here, let the component handle it
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchAdminsWithPermissions]);

  useEffect(() => {
    fetchAdminsWithPermissions();
  }, [fetchAdminsWithPermissions]);

  return {
    adminsWithPermissions,
    loading,
    error,
    fetchAdminsWithPermissions,
    getAdminPermissions,
    assignPermissionsToAdmin
  };
};